import * as React from "react";
import { cn } from "../lib/cn.js";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** Element rendered to the left inside the input. Typically an icon. */
  leadingIcon?: React.ReactNode;
  /** Element rendered to the right inside the input. */
  trailingIcon?: React.ReactNode;
  /** When true, the field is rendered in error state (red border + ring). */
  invalid?: boolean;
  /** Layout size — controls inner padding only; height is `--control-height`. */
  inputSize?: "sm" | "md";
}

/**
 * Token-driven text input. Uses semantic CSS variables for height/border/padding
 * so it tracks density mode automatically.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = "text",
      leadingIcon,
      trailingIcon,
      invalid,
      inputSize = "md",
      disabled,
      ...props
    },
    ref,
  ) => {
    const heightVar =
      inputSize === "sm" ? "var(--control-height-sm)" : "var(--control-height)";

    const wrapperBase = cn(
      "group relative flex items-center w-full",
      "rounded-[var(--radius-md)] border bg-[var(--bg-surface)]",
      "text-[var(--text-primary)]",
      "transition-[border-color,background-color] duration-[var(--duration-instant)] ease-[var(--ease-out)]",
      "focus-within:border-[var(--border-focus)]",
      "focus-within:outline focus-within:outline-2 focus-within:outline-[var(--border-focus)] focus-within:outline-offset-[-1px]",
      invalid
        ? "border-[var(--danger)] focus-within:border-[var(--danger)] focus-within:outline-[var(--danger)]"
        : "border-[var(--border-default)]",
      disabled && "opacity-60 cursor-not-allowed bg-[var(--bg-sunken)]",
    );

    const padX =
      inputSize === "sm" ? "px-[var(--space-2)]" : "px-[var(--space-3)]";

    return (
      <div className={cn(wrapperBase, className)} style={{ height: heightVar }}>
        {leadingIcon ? (
          <span
            className={cn(
              "flex shrink-0 items-center text-[var(--text-tertiary)] pl-[var(--space-3)]",
              "[&_svg]:size-4",
            )}
            aria-hidden
          >
            {leadingIcon}
          </span>
        ) : null}
        <input
          ref={ref}
          type={type}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={cn(
            "peer h-full w-full bg-transparent",
            "text-[var(--text-body-sm)] placeholder:text-[var(--text-tertiary)]",
            "outline-none border-0",
            "disabled:cursor-not-allowed",
            padX,
            leadingIcon && "pl-[var(--space-2)]",
            trailingIcon && "pr-[var(--space-2)]",
          )}
          {...props}
        />
        {trailingIcon ? (
          <span
            className={cn(
              "flex shrink-0 items-center text-[var(--text-tertiary)] pr-[var(--space-3)]",
              "[&_svg]:size-4",
            )}
            aria-hidden
          >
            {trailingIcon}
          </span>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";
